/**
 * CompanyRoutesPage · Web (B2B — Fev/2026)
 *
 * Painel full-screen dedicado às rotas de UMA empresa (scoped a `company_id`).
 *
 * Acesso:
 *   - Admin global via `/empresa/rotas?company_id=X` (a partir do
 *     CompanyDashboard → botão "Rotas")
 *   - Marshall / staff / member da empresa via mesmo path (fallback ao
 *     `user.company_id` se não passar query param)
 *
 * Features:
 *   - Lista scoped: só rotas com `company_id == X`
 *   - Pesquisa por nome + filtro de dificuldade
 *   - Upload GPX/KML (marshall/staff/admin) com flag "Enviar para os membros"
 *   - Botões "Enviar" (push) e "Ver rota" (RouteViewerModal) por rota
 *   - Top nav com botão "← Voltar ao painel da empresa"
 *
 * Zero refactor do Dashboard.js admin — reutiliza componentes existentes
 * (RouteViewerModal) mas UI é dedicada e scoped.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Route as RouteIcon, Search, Upload, Eye, Send,
  Building2, RefreshCw, X,
} from 'lucide-react';
import RouteViewerModal from '@/components/RouteViewerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Paleta Rugged consistente com CompanyDashboard.
const C = {
  bg: '#2B2A25',
  bg2: '#241E19',
  bg3: '#0F0D0B',
  brand: '#E87A2A',
  brandDim: 'rgba(232,122,42,0.16)',
  sand: '#C4A275',
  sandLine: 'rgba(196,162,117,0.18)',
  ink: '#F2EBDD',
  inkDim: '#8A7A63',
  cardBg: 'rgba(26,22,19,0.82)',
  green: '#3FA34D',
  greenDim: 'rgba(63,163,77,0.16)',
  red: '#DC2626',
};

const DIFFICULTIES = [
  { value: 'all', label: 'Todas' },
  { value: 'easy', label: 'Fácil' },
  { value: 'moderate', label: 'Moderada' },
  { value: 'hard', label: 'Difícil' },
  { value: 'extreme', label: 'Extrema' },
];

/**
 * UploadRouteModal — GPX/KML picker + campos base + push toggle.
 * Reutiliza `POST /api/routes/upload` (query params) que já aceita
 * company_admin/company_staff com auto-scope de `company_id`.
 */
const UploadRouteModal = ({ open, onClose, companyId, onUploaded, canPush }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [difficulty, setDifficulty] = useState('moderate');
  const [pushToApp, setPushToApp] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setFile(null); setName(''); setDifficulty('moderate'); setPushToApp(false);
  };

  const handleClose = () => { reset(); onClose?.(); };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith('.gpx') && !lower.endsWith('.kml')) {
      toast.error('Ficheiro tem de ser .gpx ou .kml');
      return;
    }
    setFile(f);
    if (!name) {
      // Pre-fill nome a partir do ficheiro (sem extensão)
      const base = f.name.replace(/\.(gpx|kml)$/i, '');
      setName(base);
    }
  };

  const submit = async () => {
    if (!file || !name.trim()) {
      toast.error('Escolhe ficheiro e nome');
      return;
    }
    setUploading(true);
    try {
      const params = new URLSearchParams({
        name: name.trim(),
        difficulty,
        push_to_app: pushToApp ? 'true' : 'false',
        // Fev/2026 (Ricardo) — admin global precisa de reenviar company_id
        ...(companyId && { company_id: companyId }),
      });
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/routes/upload?${params.toString()}`,
        fd,
        { withCredentials: true },
      );
      toast.success(
        pushToApp
          ? `"${res.data.name}" carregada e enviada aos membros 🔔`
          : `"${res.data.name}" carregada (privada da empresa)`,
      );
      reset();
      onUploaded?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md rounded-xl border"
        style={{ backgroundColor: C.bg2, borderColor: C.brand }}
        onClick={(e) => e.stopPropagation()}
        data-testid="upload-route-modal"
      >
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: `1px solid ${C.sandLine}` }}
        >
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5" style={{ color: C.brand }} />
            <h3 className="text-lg font-bold" style={{ color: C.ink }}>Nova rota (GPX/KML)</h3>
          </div>
          <button onClick={handleClose} data-testid="upload-modal-close">
            <X className="w-5 h-5" style={{ color: C.inkDim }} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* File picker */}
          <div>
            <label
              className="block px-3 py-6 rounded-md text-sm text-center cursor-pointer transition"
              style={{
                border: `1px dashed ${file ? C.green : C.brand}`,
                backgroundColor: file ? 'rgba(63,163,77,0.10)' : 'rgba(232,122,42,0.08)',
                color: file ? C.green : C.brand,
              }}
            >
              {file ? `✓ ${file.name}` : '📁 Escolher ficheiro .gpx ou .kml'}
              <input
                type="file"
                accept=".gpx,.kml"
                hidden
                onChange={onPickFile}
                data-testid="upload-file-input"
              />
            </label>
          </div>
          {/* Nome */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>
              Nome da rota
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Trilho da Serra do Marão"
              className="w-full mt-1 px-3 py-2 rounded-md text-sm"
              style={{
                background: 'rgba(0,0,0,0.35)', color: C.ink,
                border: `1px solid ${C.sandLine}`, outline: 'none',
              }}
              data-testid="upload-name-input"
            />
          </div>
          {/* Dificuldade */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>
              Dificuldade
            </label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {DIFFICULTIES.filter((d) => d.value !== 'all').map((d) => (
                <button
                  key={d.value}
                  onClick={() => setDifficulty(d.value)}
                  className="px-3 py-1.5 rounded text-xs font-bold transition"
                  style={{
                    backgroundColor: difficulty === d.value ? C.brand : 'rgba(0,0,0,0.35)',
                    color: difficulty === d.value ? '#fff' : C.inkDim,
                    border: `1px solid ${difficulty === d.value ? C.brand : C.sandLine}`,
                  }}
                  data-testid={`upload-diff-${d.value}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          {/* Push toggle */}
          {canPush && (
            <label
              className="flex items-start gap-2 p-3 rounded-md cursor-pointer transition"
              style={{
                backgroundColor: pushToApp ? 'rgba(63,163,77,0.10)' : 'rgba(0,0,0,0.25)',
                border: `1px solid ${pushToApp ? C.green : C.sandLine}`,
              }}
              data-testid="upload-push-toggle"
            >
              <input
                type="checkbox"
                checked={pushToApp}
                onChange={(e) => setPushToApp(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="text-xs font-bold" style={{ color: pushToApp ? C.green : C.ink }}>
                  Enviar imediatamente para os membros
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: C.inkDim }}>
                  Envia push notification a todos os membros da empresa com a nova rota disponível.
                </div>
              </div>
            </label>
          )}
          <div className="text-[11px]" style={{ color: C.inkDim }}>
            💡 A rota fica marcada como <strong style={{ color: C.brand }}>privada da empresa</strong> — só
            visível a membros com <code>company_id</code> correspondente.
          </div>
          <button
            onClick={submit}
            disabled={uploading || !file || !name.trim()}
            className="w-full py-2.5 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition disabled:opacity-60"
            style={{ backgroundColor: C.brand, color: '#fff' }}
            data-testid="upload-submit-btn"
          >
            {uploading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Upload className="w-4 h-4" />}
            {uploading ? 'A carregar…' : 'Carregar rota'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CompanyRoutesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const [company, setCompany] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [diffFilter, setDiffFilter] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewerRouteId, setViewerRouteId] = useState(null);
  const [pushingId, setPushingId] = useState(null);

  const isAdmin = user?.role === 'admin';
  const overrideCid = isAdmin ? searchParams.get('company_id') : null;
  const companyId = overrideCid || user?.company_id;

  const canUpload = ['admin', 'company_admin', 'company_staff'].includes(user?.role);

  const fetchAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [companyRes, routesRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}`, { withCredentials: true }),
        axios.get(`${API}/routes`, { withCredentials: true }),
      ]);
      setCompany(companyRes.data);
      const all = routesRes.data || [];
      setRoutes(all.filter((r) => r.company_id === companyId && !r.is_deleted));
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro a carregar rotas';
      toast.error(detail);
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    const isCompanyRole = ['company_admin', 'company_staff'].includes(user.role) && user.company_id;
    if (!isCompanyRole && !isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchAll();
  }, [user, authLoading, navigate, isAdmin, fetchAll]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return routes.filter((r) => {
      if (diffFilter !== 'all' && r.difficulty !== diffFilter) return false;
      if (q) {
        const hay = `${r.name || ''} ${r.description || ''} ${r.region || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [routes, query, diffFilter]);

  const doPushRoute = async (routeId, routeName) => {
    setPushingId(routeId);
    try {
      await axios.post(`${API}/routes/${routeId}/push`, {}, { withCredentials: true });
      toast.success(`"${routeName}" enviada aos membros`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar rota');
    } finally {
      setPushingId(null);
    }
  };

  const backHref = isAdmin && overrideCid
    ? `/empresa?company_id=${encodeURIComponent(overrideCid)}`
    : '/empresa';

  if (authLoading || (loading && routes.length === 0 && companyId)) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ backgroundColor: C.bg }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.brand }} />
      </div>
    );
  }

  if (!companyId) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center p-6"
        style={{ backgroundColor: C.bg, color: C.ink }}
        data-testid="company-routes-empty"
      >
        <div className="max-w-md text-center space-y-4">
          <Building2 className="w-16 h-16 mx-auto" style={{ color: C.brand }} />
          <h1 className="text-2xl font-bold">Nenhuma empresa seleccionada</h1>
          <p className="text-sm" style={{ color: C.inkDim }}>
            {isAdmin
              ? 'Escolhe uma empresa a gerir no botão "Empresas" no topo do Dashboard.'
              : 'Sem empresa associada. Peça a um administrador para o adicionar.'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 rounded-md font-semibold"
            style={{ backgroundColor: C.brand, color: '#fff' }}
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: C.bg }} data-testid="company-routes-page">
      {/* Header */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          backgroundColor: 'rgba(26,22,19,0.9)',
          borderBottom: `1px solid ${C.sandLine}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(backHref)}
              className="p-2 rounded-md transition"
              style={{ backgroundColor: 'rgba(0,0,0,0.35)', border: `1px solid ${C.sandLine}` }}
              title="Voltar ao painel da empresa"
              data-testid="routes-back-btn"
            >
              <ArrowLeft className="w-4 h-4" style={{ color: C.sand }} />
            </button>
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: C.brandDim, border: `1px solid ${C.brand}` }}
            >
              {company?.logo_path ? (
                <img
                  src={`${BACKEND_URL}/${company.logo_path}`}
                  alt={company.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <RouteIcon className="w-5 h-5" style={{ color: C.brand }} />
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.inkDim }}>
                Rotas da empresa
              </div>
              <div className="text-lg font-bold leading-tight" style={{ color: C.ink }}>
                {company?.name || '—'}
                <span className="ml-2 text-sm font-normal" style={{ color: C.brand }}>
                  · {routes.length} rota{routes.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ color: C.ink, border: `1px solid ${C.sandLine}` }}
              data-testid="routes-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Atualizar
            </button>
            {['admin', 'staff'].includes(user?.role) && (
              <button
                onClick={() => setUploadOpen(true)}
                className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                style={{ backgroundColor: C.brand, color: '#fff' }}
                data-testid="routes-upload-btn"
              >
                <Upload className="w-3.5 h-3.5" /> Nova rota
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Filtros */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: C.inkDim }}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Procurar por nome, descrição, região…"
              className="w-full pl-10 pr-3 py-2 rounded-md text-sm"
              style={{
                background: 'rgba(0,0,0,0.35)', color: C.ink,
                border: `1px solid ${C.sandLine}`, outline: 'none',
              }}
              data-testid="routes-search-input"
            />
          </div>
          <div className="flex gap-1.5">
            {DIFFICULTIES.map((d) => (
              <button
                key={d.value}
                onClick={() => setDiffFilter(d.value)}
                className="px-2.5 py-1.5 rounded text-xs font-bold transition"
                style={{
                  backgroundColor: diffFilter === d.value ? C.brand : 'rgba(0,0,0,0.35)',
                  color: diffFilter === d.value ? '#fff' : C.inkDim,
                  border: `1px solid ${diffFilter === d.value ? C.brand : C.sandLine}`,
                }}
                data-testid={`routes-filter-${d.value}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Lista */}
        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
        >
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm" style={{ color: C.inkDim }}>
              {routes.length === 0
                ? 'Ainda não há rotas privadas associadas a esta empresa. Carrega uma acima.'
                : 'Nenhuma rota corresponde aos filtros actuais.'}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {filtered.map((r) => (
                <div
                  key={r.route_id}
                  className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-black/10 transition"
                  data-testid={`route-row-${r.route_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>
                      {r.name}
                      {r.pushed_to_app && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded uppercase font-bold"
                          style={{ backgroundColor: C.greenDim, color: C.green }}
                        >
                          Enviada
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
                      {r.difficulty ? `${r.difficulty} · ` : ''}
                      {r.distance_km ? `${Number(r.distance_km).toFixed(1)} km · ` : ''}
                      {r.elevation_gain_m ? `${Math.round(r.elevation_gain_m)} m ganho` : ''}
                      {r.region ? ` · ${r.region}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canUpload && (
                      <button
                        onClick={() => doPushRoute(r.route_id, r.name)}
                        disabled={pushingId === r.route_id}
                        className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-60"
                        style={{ backgroundColor: C.sand, color: C.bg3 }}
                        title="Enviar para os membros"
                        data-testid={`route-push-${r.route_id}`}
                      >
                        {pushingId === r.route_id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Send className="w-3.5 h-3.5" />}
                        Enviar
                      </button>
                    )}
                    <button
                      onClick={() => setViewerRouteId(r.route_id)}
                      className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                      style={{ backgroundColor: C.brand, color: '#fff' }}
                      title="Visualizar no mapa"
                      data-testid={`route-view-${r.route_id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Ver
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modais */}
      {viewerRouteId && (
        <RouteViewerModal
          routeId={viewerRouteId}
          open={!!viewerRouteId}
          onClose={() => setViewerRouteId(null)}
          user={user}
          liveOverlay={true}
          liveOverlayMode="company"
          companyId={companyId}
        />
      )}

      <UploadRouteModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        companyId={companyId}
        canPush={canUpload}
        onUploaded={fetchAll}
      />
    </div>
  );
};

export default CompanyRoutesPage;
